import { LightningElement, wire } from "lwc";
import { logger } from "@devforce/trailhead-client-logger";
import {
  CategoryGroupFragment,
  CategoryFragment,
  CategoryType,
  CategoryGroupType,
  SearchSortType,
  SearchDirectionType,
  SearchSortKindType,
  SearchFilterInputType,
  SearchFilterDynamicArgType,
  DynamicValueSourceType,
  ProgressType,
} from "@devforce/trailhead-client-gql";
import { I18n, TranslateOptions } from "@devforce/trailhead-client-locale";
import { QueryAdapter, QueryAdapterValue, gql } from "common/graphqlAdapter";
import { FilterCollectionType, hasChildCategories } from "../util/filterHelper";
import { FormControlChangeEvent, FormControlOption } from "tds/formControl";
import {
  trackUserActivity,
  UserActivityContentResultsTabChanged,
} from "common/track";
import * as locales from "../sharedLabels/locales";
import Layout from "tds/layout";

type FilterDirection = "horizontal" | "vertical";
export type SearchTabType = "ALL" | "LEARNING" | "COMMUNITY" | "DOCUMENTATION";

const i18n = new I18n({ filters: locales });
const t = (key: string, options?: TranslateOptions) =>
  i18n.t("filters", key, options);

export default class extends LightningElement {
  filters: FilterCollectionType = {};
  loading: boolean = true;
  queryString: string | null = "";
  defaultSort: SearchSortKindType = SearchSortKindType.RelevanceType;
  sortType: SearchSortKindType | null = null;
  disabledFilters: Set<string> = new Set();
  defaultFilters: FilterCollectionType = {};
  searchTab: SearchTabType | null = null; // Null until a param sets it or the tabs are used

  private pageLocation = "lx-filterable-page";
  private validSearchTabs: Record<SearchTabType, true> = {
    ALL: true,
    LEARNING: true,
    COMMUNITY: true,
    DOCUMENTATION: true,
  };

  private progressCategories: Record<string, Record<string, string>> = {
    [t("filters.progress.options.all")]: {
      valueSource: "api_name",
      apiName: ProgressType.AllType,
      name: "All",
    },
    [t("filters.progress.options.incomplete")]: {
      valueSource: "api_name",
      apiName: ProgressType.IncompleteType,
      name: "Incomplete",
    },
    [t("filters.progress.options.completed")]: {
      valueSource: "api_name",
      apiName: ProgressType.CompletedType,
      name: "Complete",
    },
  };

  private progressFilterTypes: FilterCollectionType = {
    progress: ["All", "Incomplete", "Completed"],
  };

  get isFiltered() {
    return !!Object.keys(this.filters).length;
  }

  /**
   * Used by implementations to know when results should be rendered.
   */
  get isSetup() {
    return !this.loading;
  }

  get sort(): SearchSortType {
    return {
      type: this.sortType || this.defaultSort,
      direction: this.sortDirection,
    };
  }

  protected filterResponse: CategoryGroupType[] = [];
  private validFilterTypes: FilterCollectionType = {};
  protected initialSortValue: SearchSortKindType = this.defaultSort;

  protected get filterDirection(): FilterDirection {
    return "horizontal";
  }

  private get queryVariables() {
    return {
      query: this.queryString, // Not the GraphQL query, but the URL query string.
      filters: formatFiltersForSearch(this.filters, this.defaultFilters),
      sort: this.sort,
      tab: this.searchTab,
    };
  }

  private get filterOptions() {
    const variables: { tab?: string } = {};
    if (this.searchTab !== null) {
      variables.tab = this.searchTab;
    }

    return {
      query,
      variables,
    };
  }

  flattenCategories(categories: CategoryType[]) {
    let flatList: CategoryType[] = [];
    for (const category of categories) {
      flatList.push(category);
      if (category.categories && category.categories?.length > 0) {
        flatList = flatList.concat(this.flattenCategories(category.categories));
      }
    }
    return flatList;
  }

  // Workaround to use unlocalized apiName in url params
  private get filterNameDict() {
    return this.filterResponse.reduce(
      (acc, filterType) => {
        if (hasChildCategories(filterType)) {
          this.flattenCategories(filterType.categories).forEach((category) => {
            if (!category?.categories) {
              // If the current category is not a top-level category (i.e. not a Skill Set et al.)
              acc[category.name] = {
                // For categories inside of categories, always use the `name` to pass to Coveo instead of apiName to ensure this works for localized searches
                valueSource: DynamicValueSourceType.NameType,
                apiName: category.apiName,
                name: category.name,
              };
            }
          });
        } else {
          filterType.categories.forEach(
            (category) =>
              (acc[category.name] = {
                valueSource: filterType.dynamicValueSource,
                apiName: category.apiName,
                name: category.name,
              })
          );
        }

        return acc;
      },
      { ...this.progressCategories } as {
        [key: string]: {
          valueSource: DynamicValueSourceType;
          apiName: string;
          name: string;
        };
      }
    );
  }

  private get sortDirection() {
    switch (this.sortType) {
      case SearchSortKindType.MostPointsType:
        return SearchDirectionType.DescType;
      case SearchSortKindType.NewestType:
        return SearchDirectionType.DescType;
      default:
        return SearchDirectionType.AscType;
    }
  }

  private resetFilters() {
    this.filters = {};
    this.disabledFilters.clear();
    window.history.pushState({}, "", this.genUrl("filter"));
  }

  private resetSort() {
    this.sortType = this.initialSortValue;
    window.history.pushState({}, "", this.genUrl());
  }

  setup() {}

  connectedCallback() {
    this.initialSortValue = this.defaultSort;
    this.setQueryString();
    this.setSortParam();
    this.setSearchTabParam();
    this.setup();
  }

  // Updates to FilterCollectionType for use with contentFilterHeader
  setValidFilterTypes() {
    this.validFilterTypes = this.filterResponse.reduce(
      (acc, v) => {
        if (hasChildCategories(v)) {
          acc[v.apiName] = v.categories
            .map((category) =>
              category.categories && category.categories.length > 0
                ? category.categories.map((subCategory) => subCategory.name)
                : []
            )
            .flat();
        } else {
          acc[v.apiName] = v.categories.map((v) => v.name);
        }

        return acc;
      },
      { ...this.progressFilterTypes } as FilterCollectionType
    );
  }

  // Sets active filters and filters if there are existing url search params
  setFilters() {
    const searchParams = new URLSearchParams(window.location.search);
    const newFilters: FilterCollectionType = {};

    for (const [key, value] of searchParams) {
      // convert value from apiName to name
      let localizedValue = Object.keys(this.filterNameDict).find(
        (name) => this.filterNameDict[name].apiName === value
      );

      if (localizedValue) {
        // decode and remove numbered brackets from param key
        const decodedKey = decodeURI(key).replace(/[0-9[\]]/g, "");

        if (
          this.validFilterTypes[decodedKey] &&
          this.validFilterTypes[decodedKey].includes(localizedValue)
        ) {
          const filterValue =
            this.filterNameDict[localizedValue].valueSource ===
            DynamicValueSourceType.NameType
              ? localizedValue
              : value;
          addOrModifyCollection(newFilters, decodedKey, filterValue);
        }
      }
    }

    this.disabledFilters = getDisabledFilters(newFilters);
    this.filters = applyFilterRules(newFilters, this.defaultFilters);
  }

  setSortParam() {
    const url = new URL(window.location.href);
    let type =
      (url.searchParams.get("sort") as SearchSortKindType) ??
      this.initialSortValue;

    if (!type) {
      url.searchParams.set("sort", this.initialSortValue);
      window.history.pushState({}, "", url.href);
    } else {
      this.initialSortValue = type;
    }

    this.sortType = type;
  }

  isSearchTabType(value: string | null): value is SearchTabType {
    return this.validSearchTabs[value as SearchTabType] === true;
  }

  setSearchTabParam() {
    const url = new URL(window.location.href);
    const tab = url.searchParams.get("tab")?.toUpperCase() as SearchTabType;

    if (this.isSearchTabType(tab)) {
      this.searchTab = tab;
    } else {
      url.searchParams.delete("tab");
      window.history.pushState({}, "", url.href);
    }
  }

  setQueryString() {
    const url = new URL(window.location.href);
    this.queryString = url.searchParams.get("keywords") || "";
  }

  genUrl(updateType?: string) {
    const url = new URL(window.location.href);

    if (updateType === "filter") {
      Object.keys(this.validFilterTypes).forEach((v) => {
        this.deleteMatchingParams(url, v);
      });

      for (const [key, value] of Object.entries(this.filters)) {
        if (value) this.appendParams(url, key, value);
      }
    }
    if (this.queryString && this.queryString !== "") {
      url.searchParams.set("keywords", this.queryString);
    } else {
      url.searchParams.delete("keywords");
    }
    url.searchParams.set("sort", this.sort.type);
    if (this.searchTab != null) url.searchParams.set("tab", this.searchTab);
    return url.href;
  }

  deleteMatchingParams(url: URL, id: string) {
    const matches = [];
    for (let [paramKey] of url.searchParams) {
      if (String(paramKey).includes(id)) {
        const decodedKey = decodeURI(paramKey).replace(/[0-9[\]]/g, "");
        // check if param key without any numbered brackets matches filter category name from event
        if (decodedKey === id) {
          matches.push(paramKey);
        }
      }
    }

    // remove all params from url to replace with active filters from event detail
    matches.forEach((v) => {
      url.searchParams.delete(v);
    });
  }

  appendParams(url: URL, id: string, value: string[] | string) {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v in this.filterNameDict) {
          // convert name to unlocalized apiName
          const apiName = this.filterNameDict[v].apiName;
          apiName && url.searchParams.append(id, apiName);
        } else {
          url.searchParams.append(id, v);
        }
      });
    } else {
      url.searchParams.append(id, value);
    }
  }

  configureSortOptions(options: FormControlOption[]) {
    return options;
  }

  handleFilter(e: FormControlChangeEvent) {
    const { value, id } = e.detail;
    const filterValue = Array.isArray(value) ? value.join(",") : value;

    trackUserActivity(this, "content_results_filtered", {
      filter_api_name: id,
      filter_value: filterValue || "",
      filter_direction: this.filterDirection,
      pageLocation: this.pageLocation,
    });

    const newFilters: FilterCollectionType = Object.entries({
      ...this.filters,
      [id]: value ?? [],
    }).reduce((prev: FilterCollectionType, [key, value]) => {
      const isEmptyArray = Array.isArray(value) && !value.length;

      if (value && !isEmptyArray) {
        prev[key] = value as string[];
      }
      return prev;
    }, {} as FilterCollectionType);

    this.filters = applyFilterRules(newFilters, this.defaultFilters);
    this.disabledFilters = getDisabledFilters(this.filters);

    window.history.pushState({}, "", this.genUrl("filter"));
  }

  handleSearch(e: CustomEvent, allowEmptyDetail: boolean = false) {
    trackUserActivity(this, "content_results_searched", {
      search_term: e.detail,
      filter_direction: this.filterDirection,
      pageLocation: this.pageLocation,
    });

    if (e.detail || allowEmptyDetail) {
      this.queryString = e.detail;
      window.history.pushState({}, "", this.genUrl());
    }
  }

  handleReset() {
    const layout = this.template.querySelector(
      ".layout--element"
    ) as unknown as Layout;

    layout?.handleChildFocus();

    trackUserActivity(this, "content_results_reset", {
      filter_direction: this.filterDirection,
      pageLocation: this.pageLocation,
    });
    this.resetFilters();
  }

  handleSort(e: CustomEvent) {
    const { value } = e.detail;

    trackUserActivity(this, "content_results_sorted", {
      sort_by: value,
      filter_direction: this.filterDirection,
      pageLocation: this.pageLocation,
    });

    this.sortType = value;
    window.history.pushState({}, "", this.genUrl());
  }

  handleSearchTabChange(e: CustomEvent) {
    const tab = e.detail as string;
    if (this.searchTab !== tab) this.loading = true;

    trackUserActivity(this, "content_results_tab_changed", {
      tab_name: tab.toLowerCase(),
      pageLocation: this.pageLocation,
    } as UserActivityContentResultsTabChanged);

    this.searchTab = tab as SearchTabType;

    this.resetFilters();
    this.resetSort();
  }

  excludeFilterTypes(excludeTypes: string[]) {
    this.filterResponse = this.filterResponse.filter(
      (v) => !excludeTypes.includes(v.apiName) && v.apiName !== "tags"
    );
  }

  @wire(QueryAdapter, { options: "$filterOptions" })
  private onResult(
    result: QueryAdapterValue<{
      categoryGroups: CategoryGroupType[];
    }>
  ) {
    if (result.error) {
      logger.error(result.error.message);
    } else if (result.errors) {
      result.errors.forEach((v) => logger.error(v.message));
    } else if (result.data) {
      this.filterResponse = result.data.categoryGroups;
      this.setValidFilterTypes();
      this.setFilters();
      this.loading = false;
    }
  }
}

export const query = gql`
  query LxFilters($tab: SearchTab) {
    categoryGroups(tab: $tab) {
      ...CategoryGroup
      categories {
        ...Category
        categories {
          ...Category
        }
      }
    }
  }
  ${CategoryGroupFragment}
  ${CategoryFragment}
`;

/**
 * Adds or modifies a collection of filters.
 * If the filter key already exists, the localized value is added to the existing collection.
 * If the filter key does not exist, a new collection is created with the localized value.
 *
 * @param newCollection - The collection of filters to add or modify.
 * @param key - The key of the filter to add or modify.
 * @param value - The localized value to add to the filter collection.
 */
function addOrModifyCollection(
  newCollection: FilterCollectionType,
  key: string,
  value: string
) {
  newCollection[key] && !newCollection[key].includes(value)
    ? (newCollection[key] as string[]).push(value)
    : (newCollection[key] = [value]);
}

function applyFilterRules(
  filters: FilterCollectionType,
  defaultFilters: FilterCollectionType
) {
  if (ineligibleForChallengeType(filters)) {
    delete filters.assessments_include_hoc;
    delete filters.progress;
  }

  if (ineligibleForIntent(filters)) {
    delete filters.intent;
  }

  if (Array.isArray(filters.progress)) {
    // @ts-ignore
    filters.progress = filters.progress[0];
  }

  if (progressSelectedInSearch(filters, defaultFilters)) {
    filters.content_type = ["Module", "Project", "Superbadge"];
  }

  return filters;
}

function getDisabledFilters(filters: FilterCollectionType) {
  const disabledFilters: Set<string> = new Set();
  if (ineligibleForChallengeType(filters)) {
    disabledFilters.add("assessments_include_hoc");
    disabledFilters.add("progress");
  }
  if (ineligibleForIntent(filters)) {
    disabledFilters.add("intent");
  }
  return disabledFilters;
}

function ineligibleForIntent(filters: FilterCollectionType) {
  return !!Object.entries(filters).find(([key, value]) => {
    return (
      (key === "content_type" && !value.includes("Trail")) ||
      key === "progress" ||
      key === "assessments_include_hoc"
    );
  });
}

export function ineligibleForChallengeType(filters: FilterCollectionType) {
  return !!Object.entries(filters).find(([key, value]) => {
    return (
      key === "content_type" &&
      (value.includes("Trail") || value.includes("Trailmix"))
    );
  });
}

// if progress is selected within search content page we want to restrict content type to
// Module Project and Superbadge only since Trail and Trailmix are not supported
// unless progress is selected within an index page (defaultFilters.content_type is not null),
// or content_type is already set
export function progressSelectedInSearch(
  filters: FilterCollectionType,
  defaultFilters: FilterCollectionType
) {
  return (
    defaultFilters.content_type === undefined &&
    filters.progress !== ProgressType.AllType &&
    filters.progress !== undefined &&
    filters.content_type === undefined
  );
}

export function formatFiltersForSearch(
  filters: FilterCollectionType,
  defaults: FilterCollectionType = {}
) {
  const formatted: SearchFilterInputType = {
    dynamic: [],
    type: null,
    operator: null,
    progress: null,
  };

  for (const [key, value] of Object.entries({ ...defaults, ...filters })) {
    if (key === "content_type") {
      if (value)
        formatted.type = {
          eq: [...(value as string[]).map((v: string) => v.toUpperCase())],
        };
    } else if (key === "progress") {
      if (value) formatted.progress = value as ProgressType;
    } else {
      const filter: SearchFilterDynamicArgType = { eq: [], name: "" };
      filter.name = key;
      filter.eq = value as string[];
      formatted.dynamic!.push(filter);
    }
  }

  return formatted;
}
